import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { focusRing, NAV_CURRENT_ALPHA, NAV_HOVER_ALPHA } from '../../theme/interaction';
import { SURFACE_HEADER_HEIGHT } from '../SurfaceHeader';

// 30 and 8 steps of the 8px grid; 224 would wrap «Listas de exclusión».
export const NAV_DRAWER_WIDTH = 240;
export const NAV_DRAWER_COLLAPSED_WIDTH = 64;

const REDUCED_MOTION = { '@media (prefers-reduced-motion: reduce)': { transition: 'none' } } as const;

export const drawerSx =
  (collapsed: boolean): SxProps<Theme> =>
  (theme) => {
    const width = collapsed ? NAV_DRAWER_COLLAPSED_WIDTH : NAV_DRAWER_WIDTH;
    // Folding leaves the screen, unfolding enters it: MUI's own duration pair for drawers.
    const transition = theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: collapsed
        ? theme.transitions.duration.leavingScreen
        : theme.transitions.duration.enteringScreen,
    });
    return {
      width,
      flexShrink: 0,
      // In view under the sticky header while the page scrolls.
      position: 'sticky',
      top: SURFACE_HEADER_HEIGHT,
      alignSelf: 'flex-start',
      height: `calc(100vh - ${String(SURFACE_HEADER_HEIGHT)}px)`,
      transition,
      ...REDUCED_MOTION,
      // MUI pins a drawer's paper to the viewport; this one lives in the layout's flow.
      '& .MuiDrawer-paper': {
        position: 'relative',
        width,
        height: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box',
        backgroundColor: theme.palette.background.paper,
        borderRight: `1px solid ${theme.palette.divider}`,
        transition,
        ...REDUCED_MOTION,
      },
    };
  };

// The rows' block inset; with `rowSx`'s inline one, every row clears the clipping paper by 4px.
export const navSx: SxProps<Theme> = { py: 0.5 };

// Rows inset 4px from the paper on both axes (here inline, `navSx` above and below), because the
// paper's overflow is hidden: the ring's 2px width at a 2px offset is never clipped.
export const rowSx: SxProps<Theme> = (theme) => ({
  minHeight: 48,
  mx: 0.5,
  px: 2,
  gap: 2,
  borderRadius: 1,
  '&:hover': { backgroundColor: alpha(theme.palette.primary.main, NAV_HOVER_ALPHA) },
  '&[aria-current="page"], &[aria-current="page"]:hover': {
    backgroundColor: alpha(theme.palette.primary.main, NAV_CURRENT_ALPHA),
    color: theme.palette.primary.main,
  },
  '&[aria-current="page"] .MuiListItemIcon-root': { color: theme.palette.primary.main },
  '&.Mui-focusVisible': focusRing(theme),
});

// grey 600 (≈4.6:1 on paper), not the secondary text tone (≈2.37:1): on the folded rail the glyph is the cue.
export const iconSx: SxProps<Theme> = (theme) => ({ minWidth: 0, color: theme.palette.grey[600] });

export const labelSx: SxProps<Theme> = {
  typography: 'body2',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
